import React, { useContext, useEffect, useState } from "react";
import Swal from "sweetalert2";
import axios from "axios";
import { PlusCircle, Tags, X, CheckCircle2 } from "lucide-react";
import "../Inventory/InventoryAccordion.css";
import MyContext from "../../../MyContext";

const AddInventorySubCategoryModal = ({
  show,
  onClose,
  userID,
  baseUrl,
  selectedCategoryName, // prefilled & non-editable
  onSuccess,
}) => {
  const { config } = useContext(MyContext);

  const [form, setForm] = useState({
    subCategory: "",
    category: selectedCategoryName || "",
    subCategoryImage: "", // URL or base64; will be uploaded if base64
  });
  const [preview, setPreview] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!show) return;
    setForm((f) => ({ ...f, category: selectedCategoryName || "" })); // lock category
    setPreview(
      form.subCategoryImage && form.subCategoryImage.startsWith("data:")
        ? form.subCategoryImage
        : ""
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, selectedCategoryName]);

  if (!show) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "category") return; // locked
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleFileToBase64 = async (file) => {
    if (!file) return;
    const toBase64 = (f) =>
      new Promise((res, rej) => {
        const reader = new FileReader();
        reader.readAsDataURL(f);
        reader.onload = () => res(reader.result);
        reader.onerror = (err) => rej(err);
      });
    try {
      const b64 = await toBase64(file);
      setForm((f) => ({ ...f, subCategoryImage: b64 }));
      setPreview(b64);
    } catch (err) {
      console.error("Image encode error:", err);
      Swal.fire({ icon: "error", title: "Image Error", text: "Failed to read image file." });
    }
  };

  // EXACT upload flow from CreateOrderModal
  const uploadImageIfNeeded = async () => {
    if (form.subCategoryImage && !form.subCategoryImage.startsWith("data:")) {
      return form.subCategoryImage || "";
    }
    if (!form.subCategoryImage || !form.subCategoryImage.startsWith("data:")) return "";

    const requestBody = {
      userid: userID,
      item_name: form.subCategory || "Subcategory",
      type: "IMG",
      image_data: form.subCategoryImage,
    };

    const resp = await axios.post(
      "https://bumppy.in/anlook/api/v1/uploadItemImage.php",
      requestBody,
      config
    );

    if (resp?.data?.status === "0" && resp?.data?.image_url) {
      return resp.data.image_url;
    }
    throw new Error(resp?.data?.message || "Image upload failed");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!form.category || !form.subCategory.trim()) {
      Swal.fire({ icon: "warning", title: "Missing", text: "Subcategory and Category are required." });
      return;
    }

    try {
      setSubmitting(true);

      const imageUrl = await uploadImageIfNeeded(); // <- key point

      const payload = {
        userid: String(userID),
        subCategory: form.subCategory.trim(),
        category: form.category, // locked
        subCategoryImage: imageUrl,
      };

      const res = await axios.post(
        `${baseUrl}/api/Inventory/v1.0 /addInventorySubCategory`,
        payload,
        config
      );

      if (res?.data?.responseCode === "00") {
        Swal.fire({
          icon: "success",
          title: "Subcategory Added",
          text: res?.data?.responseMessage || "Done!",
        });
        onClose?.();
        onSuccess?.(); // parent can refresh lists
        setForm({
          subCategory: "",
          category: selectedCategoryName || "",
          subCategoryImage: "",
        });
        setPreview("");
      } else {
        Swal.fire({
          icon: "error",
          title: "Failed",
          text: res?.data?.responseMessage || "Something went wrong.",
        });
      }
    } catch (err) {
      console.error(err);
      Swal.fire({ icon: "error", title: "Error", text: err?.message || "Server error." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="inventory-modal-overlay">
      <div className="inventory-modal-box inv-itemadd-fixed-size">
        <div className="inventory-modal-header inv-itemadd-head-gradient">
          <div className="inv-itemadd-head-title">
            <PlusCircle size={20} />
            <h4>Add Subcategory</h4>
          </div>
          <button className="inventory-close-btn inv-itemadd-icon-btn" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="inventory-modal-body inv-itemadd-body-scroll">
          <section className="inv-itemadd-section">
            <div className="inv-itemadd-section-title">
              <Tags size={16} />
              <span>Details</span>
            </div>

            <div className="inv-itemadd-grid-2">
              <div className="inv-itemadd-field">
                <label>Category (locked)</label>
                <input
                  type="text"
                  name="category"
                  value={form.category}
                  readOnly
                  disabled
                  title="Prefilled from selected category"
                />
              </div>

              <div className="inv-itemadd-field">
                <label>Subcategory <span className="inv-itemadd-req">*</span></label>
                <input
                  type="text"
                  name="subCategory"
                  value={form.subCategory}
                  onChange={handleChange}
                  placeholder="e.g., Ketchup"
                  required
                />
              </div>

              <div className="inv-itemadd-field">
                <label>Image URL</label>
                <input
                  type="text"
                  name="subCategoryImage"
                  value={
                    form.subCategoryImage && !form.subCategoryImage.startsWith("data:")
                      ? form.subCategoryImage
                      : ""
                  }
                  onChange={(e) => {
                    setForm((f) => ({ ...f, subCategoryImage: e.target.value }));
                    setPreview("");
                  }}
                  placeholder="https://… (optional)"
                />
              </div>

              <div className="inv-itemadd-field">
                <label>Upload Image</label>
                <input type="file" accept="image/*" onChange={(e) => handleFileToBase64(e.target.files?.[0])} />
              </div>
            </div>

            {preview && (
              <div className="inv-itemadd-preview">
                <img src={preview} alt="preview" />
              </div>
            )}
          </section>

          <div className="inventory-modal-footer inv-itemadd-footer-sticky">
            <button type="button" className="inv-itemadd-outline" onClick={onClose} disabled={submitting}>
              <X size={16} /> Cancel
            </button>
            <button type="submit" className="inv-itemadd-cta" disabled={submitting}>
              <CheckCircle2 size={16} /> {submitting ? "Adding…" : "Add Subcategory"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddInventorySubCategoryModal;
